// native APP 相关接口

import { isVLD } from "../utils/utils"

/**
 * ios 不会向 window 中注入对象，所以在引入 native 接口的时候，需要先判断
 * 是否具有相关对象，没有则声明一个空对象
 */
;(function() {
  if (!window.bcz_system) {
    window.bcz_system = {}
  }
  if (!window.bcz_alipay) {
    window.bcz_alipay = {}
  }
  if (!window.bcz_qqpay) {
    window.bcz_qqpay = {}
  }
  if (!window.bcz_weixinpay) {
    window.bcz_weixinpay = {}
  }
  if (!window.bcz_audioPlayer) {
    window.bcz_audioPlayer = {}
  }
  if (!window.bcz_audioRecord) {
    window.bcz_audioRecord = {}
  }
  if (!window.bcz_baseInfo) {
    window.bcz_baseInfo = {}
  }
  if (!window.bcz_pageJump) {
    window.bcz_pageJump = {}
  }
})()

/**
 * 调用 native 对话框接口
 * @param {Object} options { title, content, positive, negative}
 * @description 如何使用：
 * nativeShowDialog({
 *  title: xxx,
 *  content: xxx,
 *  positive: xxx,
 *  negative: xxx
 * })
 *  .then(type => handleDialogClick(type)) // type 有两个值：'positive' 和 'negative'
 *  .catch(err => console.log(err))
 */
export function showDialog(options = {}) {
  const {
    positive = '确定',
    title = null,
    content = null,
    negative = null
  } = options
  const env = localStorage.getItem('env')
  const optionStr = JSON.stringify({ positive, title, content, negative })

  return new Promise((resolve, reject) => {
    try {
      window.bcz_system.onDialogClick = function(type) {
        resolve(type)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_showDialog.postMessage(
          optionStr
        )
      } else if (env === 'Android') {
        window.bcz_system.showDialog(options)
      } else {
        reject(new Error('Not in app'))
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 支付接口
 * 其中 type 的类型有三种 'ali', 'weixin', 'qq'
 * @param {Object} options { type, orderInfo }
 * @description 如何使用：
 * nativePay({
 *  type: 'ali',
 *  orderInfo: xxx
 * })
 *  .then(handlePayed)
 *  .catch(handleError)
 */
export function pay(options) {
  const { type = 'ali', orderInfo } = options
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    switch (type) {
      case 'ali': // 支付宝支付
        window.bcz_alipay.onPayResult = result => {
          const r = /resultStatus[ \t]*=[ \t]*[{\\"][ \t]*([0-9]+)[ \t]*[}\\"]/gi.exec(
            result
          )
          if (r[0] === 9000) {
            resolve()
          } else {
            reject(result)
          }
        }
        if (env === 'iOS') {
          window.webkit.messageHandlers.bcz_alipay_pay.postMessage(orderInfo)
        } else if (env === 'Android') {
          window.bcz_alipay.pay(orderInfo)
        } else {
          reject(new Error('Not in app'))
        }
        break
      case 'weixin': // 微信支付
        window.bcz_weixinpay.onPayResult = result => {
          const r = JSON.parse(result)
          if (r && r.errCode === 0) {
            resolve()
          } else {
            reject(r)
          }
        }
        if (env === 'iOS') {
          window.webkit.messageHandlers.bcz_weixinpay_pay.postMessage(orderInfo)
        } else if (env === 'Android') {
          window.bcz_weixinpay.pay(orderInfo)
        } else {
          reject('Not in app')
        }
        break
      case 'qq': // qq 支付
        window.bcz_qqpay.onPayResult = result => {
          const r = JSON.parse(result)
          if (r && r.errCode === 0) {
            resolve()
          } else {
            reject(r)
          }
        }
        if (env === 'iOS') {
          window.webkit.messageHandlers.bcz_qqpay_pay.postMessage(orderInfo)
        } else if (env === 'Android') {
          window.bcz_qqpay.pay(orderInfo)
        } else {
          reject(new Error('Not in app'))
        }
        break
      default:
        reject(new Error(`Wrong Pay Type: ${type}`))
    }
  })
}

/**
 * 根据 ua 提取系统的信息，可以在页面入口存储，方便区分 ios 和 android
 * 这些信息会在入口文件中获取并存入 localStorage 中，后续要使用，请直接
 * 使用 localStorage.getItem(key) 获取， 例如：
 * localStorage.getItem('env') 可以获取系统是 iOS 还是 Android
 */
export function getNativeInfo() {
  const ua = navigator.userAgent
  const patt = /bcz_app_([a-zA-Z]+)(_[a-zA-Z]+)?\/(\d*\.?\d*)/
  const baseGroup = ua.match(patt)
  let os, osVersion, appVersion, app, device
  if (baseGroup) {
    if (baseGroup[1] === 'iphone' || baseGroup[1] === 'ipad') os = 'iOS'
    else if (baseGroup[1] === 'android') os = 'Android'
    app = (baseGroup[2] && baseGroup[2].substring(1)) || 'baicizhan'
    appVersion = baseGroup[3]
  }
  const androidVersionGroup = ua.match(/android_version\/(\d*\.?\d*)/)
  if (androidVersionGroup) {
    osVersion = androidVersionGroup[1]
  }
  const androidDeviceGroup = ua.match(/device_name\/(\w*-?\w*)/)
  if (androidDeviceGroup) {
    device = androidDeviceGroup[1]
  }
  const iosGroup = ua.match(/(iPhone|iPad);.*\s+(\d+_\d+_\d+)/)
  if (iosGroup) {
    device = iosGroup[1]
    osVersion = iosGroup[2]
  }

  return { env: os, osVersion, appVersion, app, device }
}

/**
 * 使用 native 方法设置导航栏标题
 * @param {String} title 导航栏标题
 */
export function setNavigatorTitle(title = '') {
  const env = localStorage.getItem('env')

  if (env === 'iOS') {
    window.webkit.messageHandlers.bcz_system_setTitle.postMessage(
      JSON.stringify({ title })
    )
  } else if (env === 'Android') {
    window.bcz_system.setTitle(JSON.stringify({ title }))
  } else {
    document.title = title
  }
}

/**
 * 设置导航条主题
 * @param {String} backgroundColor 导航条背景色：16进制RGB颜色（如：FFFFFF）
 * @param {Integer} iconType 导航条控件以及文本主题：1 表示浅色，0 表示深色
 * @param {String} webViewBackgroundColor 是否设置 iphoneX 底部的bar 颜色
 */
export function setNavigatorBarTheme(
  backgroundColor,
  iconType,
  webViewBackgroundColor
) {
  const env = localStorage.getItem('env')
  const app = localStorage.getItem('app')
  const paramObj = {
    backgroundColor,
    textColor: '#FFFFFF',
    iconType,
    webViewBackgroundColor
  }

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_changeTheme.postMessage(
          JSON.stringify(paramObj)
        )
        resolve()
      } else if (env === 'Android') {
        if (app === 'baicizhan') {
          window.bcz_system.changeTheme(JSON.stringify(paramObj))
          resolve()
        } else if (app === 'ireading') {
          window.bcz_system.changeTheme(
            backgroundColor,
            iconType === 1 ? 'FFFFFF' : '000000'
          )
          resolve()
        }
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 打开一个新的浏览器，并载入指定页面
 * @param {String} url 指定页面 url
 * @param {String} title 指定页面的默认标题
 */
export function openWebView(url, title = null) {
  const env = localStorage.getItem('env')
  const paramObj = {
    type: 'none',
    url,
    title
  }

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_openNativeAction.postMessage(
          JSON.stringify(paramObj)
        )
      } else if (env === 'Android') {
        window.bcz_system.openNativeAction(JSON.stringify(paramObj))
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 打开客服页面
 */
export function openFeedback() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_feedback.postMessage('')
        resolve()
      } else if (env === 'Android') {
        window.bcz_system.feedback()
        resolve()
      } else {
        reject(new Error('Not in app'))
      }
    } catch (err) {
      return openWebView(
        'https://assistant.baicizhan.com/pages/feedback',
        '意见反馈'
      )
    }
  })
}

/**
 * 拷贝文本至剪切板
 * @param {String}} text 文本内容
 */
export function copyToClipboard(text) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.copy_to_clipboard.postMessage(text)
        resolve()
      } else if (env === 'Android') {
        window.bcz_system.copyToClipboard(text)
        resolve()
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 退出浏览器 (the web view of the app)
 */
export function exit() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_exit.postMessage('')
        resolve()
      } else if (env === 'Android' || isVLD) {
        window.bcz_system.exit()
        resolve()
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

// 分享相关的接口

/**
 * 设置开启和关闭分享按钮
 * @param {boolean} isEnable
 */

export function setShareState(isEnable) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_setShareState.postMessage(
          JSON.stringify({ visibility: isEnable })
        )
      } else if (env === 'Android') {
        window.bcz_system.setShareState(
          JSON.stringify({ visibility: isEnable })
        )
      } else {
        reject(new Error('Not in app'))
      }
      resolve('ok')
    } catch (err) {
      resolve(err)
    }
  })
}

/**
 *
 * @param {Object} options 分享参数，包含如下：
 * channel: 如果是全局分享，则无需指定，指定的名字必须是：
 * ['weixin', 'weixin_circle', 'qq', 'qzone', 'weibo']
 *
 * url: 分享的url
 *
 * title: 分享的标题
 *
 * desc: 描述内容，微信好友的时候需要
 *
 * img: 分享图像地址
 *
 * stat: { product: 'baicizhan', pageName: '', pageId: '' } 柯蓝统计
 */
function commitShare(options = {}) {
  const {
    channel,
    url = location.origin + location.pathname,
    title = '',
    desc = '',
    img = '',
    stat = { production: 'baicizhan', pageName: '', pageId: '' }
  } = options
  const paramObj = { channel, url, title, desc, img, stat }
  const env = window.localStorage.getItem('env')
  if (env === 'iOS') {
    window.webkit.messageHandlers.bcz_system_shareEx.postMessage(
      JSON.stringify(paramObj)
    )
  } else if (env === 'Android') {
    window.bcz_system.shareEx(JSON.stringify(paramObj))
  } else {
    throw new Error('Not in app')
  }
}

/**
 * 全局分享
 *
 * @param {Object}} options 分享参数设置
 * @param {Function} onShareSend 分享动作回调，用于统计
 * @param {Function} onShareSuccess 分享成功，不一定靠谱
 * @param {Function} onShareFail 分享失败
 *
 * 某个页面需要分享，只需全局调用该方法即可
 */
export function globalShare(options, onShareSend, onShareSuccess, onShareFail) {
  window.bcz_system.onShareClick = () => {
    commitShare(options)
  }

  window.bcz_system.onShareSend = () => {
    typeof onShareSend === 'function' ? onShareSend() : null
  }

  window.bcz_system.onShare = result => {
    try {
      result = JSON.parse(result)
      if (result.errCode === 0) {
        typeof onShareSuccess === 'function' ? onShareSuccess() : null
      } else {
        typeof onShareFail === 'function' ? onShareFail() : null
      }
    } catch (err) {
      typeof onShareFail === 'function' ? onShareFail() : null
    }
  }
}

/**
 * 某个渠道的指定分享
 * 需要在 options 中包含 channel, 且 channel 具体包含如下：
 * 'session' 微信好友
 * 'timeline' 微信朋友圈
 * 'qq_session' QQ 好友
 * 'qq_timeline', QQ 空间
 * 'weibo', 微博
 * @param {Object} options 分享参数设置
 * @param {Function} onShareSend 分享动作回调，用于统计, 可选参数
 *
 * @description 用法如下
 * chanelShare(options, onShareSend)
 *  .then(channel => handleChanle(chanel))
 *  .catch(err => console.log(err))
 */
export function channelShare(options, onShareSend) {
  return new Promise((resolve, reject) => {
    window.bcz_system.onShare = (result, channel) => {
      try {
        result = JSON.parse(result)
        channel = JSON.parse(channel)
        if (result.errCode === 0) resolve(channel)
        else reject()
      } catch (err) {
        reject(err)
      }
    }

    window.bcz_system.onShareSend = () => {
      typeof onShareSend === 'function' ? onShareSend() : null
    }

    commitShare(options)
  })
}

/**
 * 获取可用的分享渠道
 * @description 用法说明
 * getAvailableChannels().then(callback)
 */
export function getAvailableChannels() {
  const env = localStorage.setItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_system.onGetShareableChannels = channels => {
        resolve(channels)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_system_getShareableChannels.postMessage(
          ''
        )
      } else if (env === 'Android') {
        window.bcz_system.getShareableChannels()
      } else {
        reject(new Error('Not in app'))
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 调用 native 播放接口
 * @param {String} url
 * @param {Function} onStart 开始播放的回调
 * @param {Function} onComplete 播放结束之后的回调
 * @param {Function} onError 播放错误出现的回调
 */
export function audioPlay(url, onStart, onComplete, onError) {
  const env = localStorage.getItem('env')
  return new Promise((resolve, reject) => {
    window.bcz_audioPlayer.onStart = onStart
    window.bcz_audioPlayer.onComplete = onComplete
    window.bcz_audioPlayer.onError = onError
    if (env === 'iOS') {
      window.webkit.messageHandlers.bcz_audioPlayer_start.postMessage(url)
    } else if (env === 'Android') {
      window.bcz_audioPlayer.start(url)
    } else {
      if (onComplete) onComplete()
      reject(new Error('Not in app'))
    }
    resolve()
  })
}

/**
 * 停止音频
 */
export function audioStop() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioPlayer.onStop = function() {
        resolve()
      }
      window.bcz_audioPlayer.onError = function(err) {
        reject(err)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioPlayer_stop.postMessage('')
      } else if (env === 'Android') {
        window.bcz_audioPlayer.stop()
      } else {
        throw Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 暂停音频
 */
export function audioPause() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioPlayer.onPause = function() {
        resolve()
      }
      window.bcz_audioPlayer.onError = function(err) {
        reject(err)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioPlayer_pause.postMessage('')
      } else if (env === 'Android') {
        window.bcz_audioPlayer.pause()
      } else {
        throw Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 恢复音频
 */
export function audioResume() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioPlayer.onResume = function() {
        resolve()
      }
      window.bcz_audioPlayer.onError = function(err) {
        reject(err)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioPlayer_resume.postMessage('')
      } else if (env === 'Android') {
        window.bcz_audioPlayer.resume()
      } else {
        throw Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 指定音频前进多少毫秒开始播放
 * @param {Number} mils 毫秒数
 */
export function audioSeek(mils) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioPlayer.onSeek = function() {
        resolve()
      }
      window.bcz_audioPlayer.onError = function(err) {
        reject(err)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioPlayer_seek.postMessage(mils)
      } else if (env === 'Android') {
        window.bcz_audioPlayer.seek(mils)
      } else {
        throw Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 开始录音回调
 * @param {Function} onStart 开始录音回调
 * @param {Function} onStop 结束录音回调
 * @param {Function} onError 录音错误回调
 */
export function recordStart(onStart, onStop, onError) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioRecord.onStart = onStart
      window.bcz_audioRecord.onStop = onStop
      window.bcz_audioRecord.onError = onError

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioRecord_startRecord.postMessage(
          ''
        )
      } else if (env === 'Android') {
        window.bcz_audioRecord.startRecord()
      } else {
        throw new Error('Not in app')
      }
      resolve()
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 停止录音
 */
export function recordStop() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_audioRecord.onStop = function() {
        resolve()
      }

      window.bcz_audioRecord.onError = function(err) {
        reject(err)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_audioRecord_stopRecord.postMessage('')
      } else if (env === 'Android') {
        window.bcz_audioRecord.stopRecord()
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 预下载资源
 * @param {Array} urlList 要下载的资源的数组
 * @param {Function} callback(url, status) 下载数据会回调
 *
 * @description
 * 这个接口是没有回调的，只管调用就行了
 * 捕获的错误是接口层面的错误
 */
export function preload(urlList, callback) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_baseInfo.onPreload = function(url, status) {
        if (typeof callback === 'function') {
          callback(url, status)
        }
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_baseInfo_preload.postMessage(urlList)
      } else if (env === 'Android') {
        window.bcz_baseInfo.preload(urlList)
      } else {
        throw new Error('Not in app')
      }
      resolve()
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 上传学习记录
 * @param {Object} record {id:"123",score:"3", spanDay:"0",todayNew:"true"}
 * 注意： 这里的数字 bool 类型都是字符串，因为 Android 解析数字有问题
}
 */
export function uploadLearningRecord(record) {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_baseInfo.learningRecordSuccess = function(id) {
        resolve(id)
      }
      window.bcz_baseInfo.learningRecordFailed = function(msg) {
        reject(new Error(msg))
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_baseInfo_learningRecord.postMessage(
          JSON.stringify(record)
        )
      } else if (env === 'Android') {
        window.bcz_baseInfo.learningRecord(JSON.stringify(record))
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * H5 完成题型之后调用该接口进行进行打卡
 */
export function requestDaka() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_pageJump.requestDakaSuccess = function() {
        resolve()
      }
      window.bcz_pageJump.requestDakaFailed = function(msg) {
        reject(new Error(msg))
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_pageJump_requestDaka.postMessage('')
      } else if (env === 'Android') {
        window.bcz_pageJump.requestDaka()
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * 提供蛋糕题型的所有数据
 */
export function requestLearningInfo() {
  const env = localStorage.getItem('env')

  return new Promise((resolve, reject) => {
    try {
      window.bcz_baseInfo.onRequestLearningInfo = function(obj) {
        resolve(obj)
      }

      if (env === 'iOS') {
        window.webkit.messageHandlers.bcz_baseInfo_requestLearningInfo('')
      } else if (env === 'Android') {
        window.bcz_baseInfo.requestLearningInfo()
      } else {
        throw new Error('Not in app')
      }
    } catch (err) {
      reject(err)
    }
  })
}
